import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { FiGrid, FiUser, FiChevronDown, FiSearch, FiX, FiExternalLink } from "react-icons/fi";
import AdminTopBar from "./AdminTopBar";
import { authFetch, fetchSiteIdentity } from "../api";
import defaultLogo from "../assets/logo.png";
import { NAV_SECTIONS, PAGE_GROUPS, badgeValue, searchLinks, sectionBadge, sectionOfPath, websitePages } from "./adminNav";

const STORE = "admin-nav-open";

function loadOpen() {
  try { return JSON.parse(localStorage.getItem(STORE) || "{}") || {}; } catch { return {}; }
}

function linkClass({ isActive }) {
  return `flex items-center gap-2.5 pl-3 pr-2.5 py-2 rounded-lg text-[13px] font-medium transition-colors ${isActive ? "bg-brand-gradient text-white" : "text-gray-300 hover:bg-white/5 hover:text-white"}`;
}

function Badge({ n, tone = "warn" }) {
  if (!n) return null;
  return <span className={`ml-auto min-w-[20px] text-center text-[10.5px] font-bold tabular-nums px-1.5 py-0.5 rounded-full ${tone === "warn" ? "bg-amber-400 text-navy-dark" : "bg-white/15 text-gray-100"}`} aria-label={`${n} waiting`}>{n > 99 ? "99+" : n}</span>;
}

function TopLink({ to, icon: Icon, children, onNavigate }) {
  return <NavLink to={to} onClick={onNavigate} className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-brand-gradient text-white" : "text-gray-300 hover:bg-white/5 hover:text-white"}`}><Icon aria-hidden="true" /> {children}</NavLink>;
}

// One section of the menu: a coloured header with what is waiting inside it, and its links.
function Section({ section, open, onToggle, badges, menu, onNavigate }) {
  const Icon = section.icon;
  const total = sectionBadge(section, badges);
  const panelId = `nav-${section.id}`;
  return (
    <div>
      <button type="button" onClick={onToggle} aria-expanded={open} aria-controls={panelId}
        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-gray-200 hover:bg-white/5 transition-colors">
        <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${section.color}26`, color: section.color }}><Icon aria-hidden="true" size={15} /></span>
        <span className="flex-1 text-left truncate">{section.label}</span>
        {!open && <Badge n={total} />}
        <FiChevronDown aria-hidden="true" size={15} className={`text-gray-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div id={panelId} className="ml-[22px] pl-3 border-l space-y-0.5 mt-0.5 mb-1.5" style={{ borderColor: `${section.color}55` }}>
          {section.pages && websitePages(menu).map((page) => {
            const group = PAGE_GROUPS[page.slug];
            if (!group) return <NavLink key={page.id} to={`/admin/pages/${page.slug}`} onClick={onNavigate} className={linkClass}>{page.title}</NavLink>;
            return <PageGroup key={page.id} title={page.title} links={group} onNavigate={onNavigate} />;
          })}
          {section.links.map((l) => (
            <NavLink key={l.to} to={l.to} end onClick={onNavigate} className={linkClass}>
              <span className="truncate">{l.label}</span><Badge n={badgeValue(badges, l.badge)} tone={l.quiet ? "quiet" : "warn"} />
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

function PageGroup({ title, links, onNavigate }) {
  const location = useLocation();
  const inside = links.some((l) => location.pathname === l.to);
  const [open, setOpen] = useState(inside);
  useEffect(() => { if (inside) setOpen(true); }, [inside]);
  return (
    <div>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="w-full flex items-center gap-2.5 pl-3 pr-2.5 py-2 rounded-lg text-[13px] font-medium text-gray-300 hover:bg-white/5 hover:text-white transition-colors">
        <span className="flex-1 text-left truncate">{title}</span><FiChevronDown aria-hidden="true" size={13} className={`text-gray-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="ml-3 pl-3 border-l border-navy-border space-y-0.5">{links.map((l) => <NavLink key={l.to} to={l.to} end onClick={onNavigate} className={linkClass}>{l.label}</NavLink>)}</div>}
    </div>
  );
}

export default function AdminLayout() {
  const location = useLocation();
  const [menu, setMenu] = useState([]);
  const [badges, setBadges] = useState(null);
  const [logoUrl, setLogoUrl] = useState(defaultLogo);
  const [open, setOpen] = useState(() => ({ ...loadOpen(), ...(sectionOfPath(window.location.pathname) ? { [sectionOfPath(window.location.pathname)]: true } : {}) }));
  const [query, setQuery] = useState("");
  const [drawer, setDrawer] = useState(false);

  useEffect(() => {                                                          // dark scroll bars while the admin panel is open
    const root = document.documentElement;
    const before = root.style.colorScheme;
    root.style.colorScheme = "dark";
    return () => { root.style.colorScheme = before; };
  }, []);

  useEffect(() => {
    authFetch("/core/menu/").then((r) => r.json()).then((items) => setMenu(Array.isArray(items) ? items : [])).catch(() => {});
    fetchSiteIdentity().then((identity) => { if (identity.logo) setLogoUrl(identity.logo); }).catch(() => {});
  }, []);

  // what is waiting for a person, shown as numbers beside the links: asked again every minute
  const loadBadges = useCallback(() => { authFetch("/core/dashboard/?light=1").then((r) => (r.ok ? r.json() : null)).then((d) => { if (d && d.badges) setBadges(d.badges); }).catch(() => {}); }, []);
  useEffect(() => { loadBadges(); const t = setInterval(loadBadges, 60000); return () => clearInterval(t); }, [loadBadges]);
  useEffect(() => { loadBadges(); }, [location.pathname, loadBadges]);

  // the section of the page being edited opens by itself
  useEffect(() => { const id = sectionOfPath(location.pathname); if (id) setOpen((o) => (o[id] ? o : { ...o, [id]: true })); setDrawer(false); }, [location.pathname]);
  useEffect(() => { try { localStorage.setItem(STORE, JSON.stringify(open)); } catch { /* the menu simply forgets what was open */ } }, [open]);
  useEffect(() => { if (!drawer) return undefined; const onKey = (e) => { if (e.key === "Escape") setDrawer(false); }; document.addEventListener("keydown", onKey); return () => document.removeEventListener("keydown", onKey); }, [drawer]);

  const notifications = useMemo(() => {
    const b = badges || {};
    return [
      ["Help requests to review", b.foundation_requests, "/admin/pages/foundation/requests"], ["Help offers to confirm", b.foundation_offers, "/admin/pages/foundation/offers"],
      ["Support tickets open", b.tickets_open, "/admin/pages/ai-tools/chatbots"], ["New leads", b.leads_new, "/admin/pages/ai-tools/chatbots"], ["WhatsApp chats waiting", b.chats_waiting, "/admin/pages/ai-tools/chatbots"],
    ].filter(([, n]) => n).map(([label, count, to]) => ({ label, count, to }));
  }, [badges]);
  const found = useMemo(() => searchLinks(menu, query), [menu, query]);
  const toggle = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));
  const closeDrawer = () => setDrawer(false);

  const sidebar = (
    <>
      <div className="h-16 shrink-0 flex items-center justify-between px-5 border-b border-navy-border">
        <img src={logoUrl} alt="Mr. Aali Online Technologies" className="h-8" />
        <button type="button" className="lg:hidden w-9 h-9 flex items-center justify-center rounded-lg text-gray-300 hover:bg-white/5" onClick={closeDrawer} aria-label="Close the menu"><FiX /></button>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" aria-label="Admin menu">
        <TopLink to="/admin/dashboard" icon={FiGrid} onNavigate={closeDrawer}>Dashboard</TopLink>
        <TopLink to="/admin/profile" icon={FiUser} onNavigate={closeDrawer}>Profile</TopLink>

        <div className="relative my-3">
          <FiSearch aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={14} />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a page..." aria-label="Find a page in the menu" className="w-full bg-navy-dark border border-navy-border rounded-lg pl-9 pr-8 py-2 text-[13px] outline-none focus:border-accent-light placeholder-gray-500" />
          {query && <button type="button" onClick={() => setQuery("")} aria-label="Clear the search" className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"><FiX size={14} /></button>}
        </div>

        {query.trim() ? (
          <div className="space-y-0.5" role="status">
            {!found.length && <p className="text-xs text-gray-500 px-3 py-2">Nothing matches "{query}".</p>}
            {found.map((l) => {
              const section = NAV_SECTIONS.find((s) => s.id === l.section);
              return (
                <NavLink key={l.to} to={l.to} end onClick={() => { setQuery(""); closeDrawer(); }} className={linkClass}>
                  <span aria-hidden="true" className="w-2 h-2 rounded-full shrink-0" style={{ background: section.color }} />
                  <span className="truncate">{l.group ? `${l.group}: ` : ""}{l.label}</span><Badge n={badgeValue(badges, l.badge)} />
                </NavLink>
              );
            })}
          </div>
        ) : (
          NAV_SECTIONS.map((section) => <Section key={section.id} section={section} open={!!open[section.id]} onToggle={() => toggle(section.id)} badges={badges} menu={menu} onNavigate={closeDrawer} />)
        )}
      </nav>
      <div className="shrink-0 p-3 border-t border-navy-border">
        <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] text-gray-300 hover:bg-white/5 hover:text-white"><FiExternalLink aria-hidden="true" /> View the website</a>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-navy-darker text-white">
      <aside className="hidden lg:flex w-64 xl:w-72 shrink-0 bg-navy border-r border-navy-border flex-col sticky top-0 h-screen">{sidebar}</aside>
      {drawer && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={closeDrawer} aria-hidden="true" />
          <aside className="relative w-72 max-w-[85vw] bg-navy border-r border-navy-border flex flex-col h-full shadow-2xl" role="dialog" aria-modal="true" aria-label="Menu">{sidebar}</aside>
        </div>
      )}
      <div className="flex-1 flex flex-col min-w-0">
        <AdminTopBar onMenu={() => setDrawer(true)} notifications={notifications} unreadMessages={(badges && badges.contact_unread) || 0} />
        <main className="flex-1 p-4 md:p-8 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
